import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Users, ArrowUpRight, Waves } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { useClubs } from "@/hooks/useClubs";
import { getDefaultThumbnail } from "@/lib/strapi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export default function ClubsPage() {
  const { clubs, isLoading, error } = useClubs(400);
  console.log(clubs);
  const fallbackThumbnail = getDefaultThumbnail();

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [filterCountry, setFilterCountry] = useState("All");

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const countries = [
    "All",
    ...Array.from(new Set(clubs.map((club) => club.country).filter(Boolean))),
  ];

  const filtered = filterCountry === "All" ? clubs : clubs.filter((c) => c.country === filterCountry);
  const getClubPath = (club) => `/clubs/${club?.slug || club?.id}`;

  return (
    <div data-testid="clubs-page" className="min-h-screen">
      <Header />
      <PageBanner
        eyebrow="Clubs Directory"
        title="Find Your Club"
        description="Browse APAC swimming clubs, schools, and academies. Connect with your local swimming community."
        image="https://images.unsplash.com/photo-1530549387789-4c1017266635?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Filters */}
          <div className="mb-12">
            <label className="font-inter font-light text-xs tracking-[0.15em] uppercase text-warm-gray mb-3 block">
              Filter by Country
            </label>
            <div className="flex flex-wrap gap-2" data-testid="clubs-country-filter">
              {countries.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilterCountry(c)}
                  data-testid={`clubs-filter-${c.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-inter font-medium text-xs px-4 py-2 rounded-sm border transition-colors duration-150 ${
                    filterCountry === c
                      ? "bg-solar-orange text-near-black border-solar-orange"
                      : "bg-transparent text-near-black/50 border-near-black/10 hover:border-near-black/30 hover:text-near-black"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Club Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            key={filterCountry}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {isLoading && (
              <div className="md:col-span-2 lg:col-span-4 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                Loading clubs...
              </div>
            )}

            {!!error && !isLoading && (
              <div className="md:col-span-2 lg:col-span-4 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                Unable to load clubs right now. Please try again shortly.
              </div>
            )}

            {!isLoading && !error && filtered.map((club) => (
              <motion.div key={club.id} variants={itemVariants}>
                <Link
                  to={getClubPath(club)}
                  data-testid={`club-card-${club.id}`}
                  className="group bg-white rounded-sm overflow-hidden border border-near-black/[0.08] hover:-translate-y-1 transition-transform duration-200 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={club.image}
                      alt={club.name}
                      loading="lazy"
                      onError={handleImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-near-black/60 to-transparent p-4">
                      <div className="flex items-center gap-1.5 text-white/80">
                        <MapPin size={12} />
                        <span className="font-inter font-light text-xs">{club.city}, {club.country}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-inter font-semibold text-base text-near-black tracking-tight leading-snug mb-3 group-hover:text-amber-dusk transition-colors duration-150">
                      {club.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 bg-amber-pale text-amber-dusk font-inter font-medium text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm">
                        <Waves size={10} />
                        {club.facility}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-amber-pale text-amber-dusk font-inter font-medium text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm">
                        {club.level}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-warm-gray mt-auto">
                      <Users size={12} />
                      <span className="font-inter font-light text-xs">{club.members} members</span>
                    </div>
                    <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk mt-4 group-hover:gap-2 transition-all duration-150">
                      Read profile
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {!isLoading && !error && filtered.length === 0 && (
            <p className="font-inter font-normal text-base text-warm-gray text-center py-16">
              No clubs found for the selected filter. Try a different country.
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
